import { Injectable } from '@angular/core';
import { PlatformService } from '../../common/domain/platform/platform.service';
import { FilesystemService } from '../../filesystem/domain/filesystem.service';
import { LocalFileStore } from '../../file-download/domain/local-file-store';
import { SessionService } from '../../session/domain/session.service';

@Injectable({
  providedIn: 'root',
})
export class GarbageCollectorService {
  private static readonly CACHE_MAX_AGE_DAYS = 7; // ATM, task description files
  private static readonly DIRECTORIES_TO_DELETE_MAX_AGE_DAYS = 3; // ATM, task directories

  constructor(
    private readonly platformService: PlatformService,
    private readonly filesystemService: FilesystemService,
    private readonly sessionService: SessionService,
    private readonly localFileStore: LocalFileStore
  ) {}

  async runOnResumeIfNeeded(): Promise<void> {
    const shouldRun = await this.shouldRunNow();
    if (!shouldRun) {
      return;
    }

    await this.executeCleanup();
    await this.registerExecution(Date.now());
  }

  async shouldRunNow(referenceTimestamp: number = Date.now()): Promise<boolean> {
    const lastExecutionTimestamp = this.sessionService.getGarbageCollectorLastExecutionTimestamp();
    if (lastExecutionTimestamp === null) {
      return true;
    }

    return !this.isSameDay(lastExecutionTimestamp, referenceTimestamp);
  }

  async executeCleanup(referenceTimestamp: number = Date.now()): Promise<void> {
    const filesCutoff = referenceTimestamp - this.daysToMilliseconds(GarbageCollectorService.CACHE_MAX_AGE_DAYS);

    await this.localFileStore.pruneFilesOlderThan(filesCutoff);
    await this.pruneQueuedDirectories(referenceTimestamp);
  }

  async registerExecution(executedAt: number = Date.now()): Promise<void> {
    await this.sessionService.saveGarbageCollectorLastExecutionTimestamp(executedAt);
  }

  async addDirectoryToDelete(directory: string): Promise<void> {
    await this.sessionService.addGarbageCollectorDirectoryToDelete(directory, Date.now());
  }

  private async pruneQueuedDirectories(referenceTimestamp: number): Promise<void> {
    const queuedDirectories = this.sessionService.getGarbageCollectorDirectoriesToDelete();
    if (queuedDirectories.length === 0) {
      return;
    }

    const deletionCutoff =
      referenceTimestamp - this.daysToMilliseconds(GarbageCollectorService.DIRECTORIES_TO_DELETE_MAX_AGE_DAYS);
    const remainingDirectories = [];

    for (const queuedDirectory of queuedDirectories) {
      if (queuedDirectory.createdAt >= deletionCutoff) {
        remainingDirectories.push(queuedDirectory);
        continue;
      }

      if (this.platformService.isCordova()) {
        await this.filesystemService.deleteDirectory(queuedDirectory.directory);
      }
    }

    await this.sessionService.saveGarbageCollectorDirectoriesToDelete(remainingDirectories);
  }

  private isSameDay(timestampA: number, timestampB: number): boolean {
    const dateA = new Date(timestampA);
    const dateB = new Date(timestampB);

    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  }

  private daysToMilliseconds(days: number): number {
    return days * 24 * 60 * 60 * 1000;
  }
}
